/** Task & Action Management — categories, meta maps, and filter definitions. */
import type { BadgeVariant } from '@/components/ui/Badge'
import type { ActionPriority, ActionStatus, ActionCategoryGroup } from './types'

/** The Minister's office staff — the pool for assigning / delegating tasks. */
export const OFFICE_STAFF: { id: string; name: string }[] = [
  { id: 'staff-debabrata', name: 'Debabrata' },
  { id: 'staff-dilip', name: 'Dilip Da' },
  { id: 'staff-ps', name: 'Private Secretary' },
  { id: 'staff-ulganathan', name: 'Ulganathan' },
  { id: 'staff-meena', name: 'Meena' },
  { id: 'staff-backoffice', name: 'Back Office' },
  { id: 'staff-debangshu', name: 'Debangshu' },
]

const STAFF_MAP: Record<string, string> = Object.fromEntries(OFFICE_STAFF.map((s) => [s.id, s.name]))

export function staffName(id?: string): string {
  return id ? STAFF_MAP[id] ?? '—' : '—'
}

export const GROUP_META: Record<ActionCategoryGroup, { label: string }> = {
  communication: { label: 'Communication' },
  meeting_prep: { label: 'Meeting Preparation' },
  administrative: { label: 'Administrative' },
  office_ops: { label: 'Office Operations' },
  personal_reminder: { label: 'Personal Reminders' },
  other: { label: 'Others' },
}

export interface CategoryDef {
  id: string
  label: string
  group: ActionCategoryGroup
  /** Typical minutes to complete — used to pre-fill "Expected duration". */
  minutes?: number
}

/** Configurable task categories (seed defaults). */
export const CATEGORIES: CategoryDef[] = [
  // Communication
  { id: 'make_call', label: 'Make Phone Call', group: 'communication', minutes: 5 },
  { id: 'return_call', label: 'Return Phone Call', group: 'communication', minutes: 5 },
  { id: 'followup_call', label: 'Follow-up Call', group: 'communication', minutes: 5 },
  { id: 'send_letter', label: 'Send Letter', group: 'communication', minutes: 15 },
  { id: 'send_email', label: 'Send Email', group: 'communication', minutes: 10 },
  { id: 'send_invitation', label: 'Send Invitation', group: 'communication', minutes: 10 },
  { id: 'send_congratulations', label: 'Send Congratulations', group: 'communication', minutes: 5 },
  { id: 'send_condolence', label: 'Send Condolence Message', group: 'communication', minutes: 5 },
  // Meeting Preparation
  { id: 'prepare_briefing', label: 'Prepare Briefing Note', group: 'meeting_prep', minutes: 30 },
  { id: 'print_documents', label: 'Print Documents', group: 'meeting_prep', minutes: 10 },
  { id: 'arrange_room', label: 'Arrange Meeting Room', group: 'meeting_prep', minutes: 15 },
  { id: 'arrange_vc', label: 'Arrange Video Conference', group: 'meeting_prep', minutes: 15 },
  { id: 'arrange_refreshments', label: 'Arrange Refreshments', group: 'meeting_prep', minutes: 15 },
  { id: 'arrange_food', label: 'Arrange Food', group: 'meeting_prep', minutes: 20 },
  { id: 'arrange_security', label: 'Arrange Security', group: 'meeting_prep', minutes: 20 },
  { id: 'arrange_vehicle', label: 'Arrange Vehicle', group: 'meeting_prep', minutes: 15 },
  { id: 'confirm_attendance', label: 'Confirm Attendance', group: 'meeting_prep', minutes: 10 },
  { id: 'prepare_presentation', label: 'Prepare Presentation', group: 'meeting_prep', minutes: 45 },
  // Administrative
  { id: 'review_file', label: 'Review File', group: 'administrative', minutes: 15 },
  { id: 'obtain_signature', label: 'Obtain Signature', group: 'administrative', minutes: 5 },
  { id: 'collect_report', label: 'Collect Report', group: 'administrative', minutes: 10 },
  { id: 'submit_report', label: 'Submit Report', group: 'administrative', minutes: 10 },
  { id: 'prepare_documents', label: 'Prepare Documents', group: 'administrative', minutes: 20 },
  { id: 'followup_department', label: 'Follow-up with Department', group: 'administrative', minutes: 10 },
  { id: 'obtain_approval', label: 'Obtain Approval', group: 'administrative', minutes: 10 },
  { id: 'dispatch_documents', label: 'Dispatch Documents', group: 'administrative', minutes: 10 },
  // Office Operations
  { id: 'arrange_accommodation', label: 'Arrange Accommodation', group: 'office_ops', minutes: 20 },
  { id: 'book_travel', label: 'Book Travel', group: 'office_ops', minutes: 20 },
  { id: 'purchase_items', label: 'Purchase Items', group: 'office_ops', minutes: 20 },
  { id: 'arrange_gifts', label: 'Arrange Gifts', group: 'office_ops', minutes: 20 },
  { id: 'arrange_flowers', label: 'Arrange Flowers', group: 'office_ops', minutes: 10 },
  { id: 'coordinate_media', label: 'Coordinate Media', group: 'office_ops', minutes: 30 },
  { id: 'coordinate_protocol', label: 'Coordinate Protocol', group: 'office_ops', minutes: 30 },
  { id: 'coordinate_logistics', label: 'Coordinate Event Logistics', group: 'office_ops', minutes: 45 },
  // Personal Reminders
  { id: 'remind_minister', label: 'Remind Minister', group: 'personal_reminder', minutes: 5 },
  // Others
  { id: 'other', label: 'Other', group: 'other', minutes: 15 },
]

export const CATEGORY_MAP: Record<string, CategoryDef> = Object.fromEntries(
  CATEGORIES.map((c) => [c.id, c]),
)

export function categoryLabel(id: string): string {
  return CATEGORY_MAP[id]?.label ?? id
}
export function categoryGroup(id: string): ActionCategoryGroup | undefined {
  return CATEGORY_MAP[id]?.group
}

export const PRIORITY_META: Record<ActionPriority, { label: string; variant: BadgeVariant; order: number }> = {
  critical: { label: 'Critical', variant: 'red', order: 0 },
  high: { label: 'High', variant: 'orange', order: 1 },
  medium: { label: 'Medium', variant: 'amber', order: 2 },
  low: { label: 'Low', variant: 'slate', order: 3 },
}
export const PRIORITIES = Object.keys(PRIORITY_META) as ActionPriority[]

export const STATUS_META: Record<ActionStatus, { label: string; variant: BadgeVariant }> = {
  draft: { label: 'Draft', variant: 'slate' },
  not_started: { label: 'Not Started', variant: 'slate' },
  assigned: { label: 'Assigned', variant: 'blue' },
  in_progress: { label: 'In Progress', variant: 'amber' },
  waiting: { label: 'Waiting', variant: 'purple' },
  on_hold: { label: 'On Hold', variant: 'orange' },
  completed: { label: 'Completed', variant: 'green' },
  cancelled: { label: 'Cancelled', variant: 'red' },
}
export const STATUSES = Object.keys(STATUS_META) as ActionStatus[]

/** Statuses that count as "still open". */
export const OPEN_STATUSES: ActionStatus[] = ['draft', 'not_started', 'assigned', 'in_progress', 'waiting', 'on_hold']

export const MINISTER_OUTCOME_META = {
  completed: { label: 'Completed', variant: 'green' as BadgeVariant },
  deferred: { label: 'Deferred', variant: 'amber' as BadgeVariant },
  rescheduled: { label: 'Rescheduled', variant: 'blue' as BadgeVariant },
  unable: { label: 'Unable to Complete', variant: 'red' as BadgeVariant },
}

/** Quick filters shown as chips on the list page. */
export type FilterId =
  | 'all'
  | 'today'
  | 'tomorrow'
  | 'this_week'
  | 'overdue'
  | 'pending'
  | 'completed'
  | 'assigned_to_me'
  | 'high_priority'
  | 'g_communication'
  | 'g_meeting_prep'
  | 'g_administrative'
  | 'g_personal_reminder'

export const QUICK_FILTERS: { id: FilterId; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'today', label: 'Today' },
  { id: 'tomorrow', label: 'Tomorrow' },
  { id: 'this_week', label: 'This Week' },
  { id: 'overdue', label: 'Overdue' },
  { id: 'pending', label: 'Pending' },
  { id: 'completed', label: 'Completed' },
  { id: 'assigned_to_me', label: 'Assigned to Me' },
  { id: 'high_priority', label: 'High Priority' },
  { id: 'g_communication', label: 'Calls' },
  { id: 'g_meeting_prep', label: 'Meeting Prep' },
  { id: 'g_administrative', label: 'Administrative' },
  { id: 'g_personal_reminder', label: 'Personal Reminders' },
]
